<template>
  <f7-page name="create-ad-campaign">
    <f7-navbar :title="$t('adCreate.title')" back-link no-shadow >
      <f7-nav-left></f7-nav-left>
      <f7-nav-right
        ><span class="step-label"
          >{{ $t("common.creationStep") }} {{ step }}/{{ totalSteps }}</span
        ></f7-nav-right
      >
    </f7-navbar>
    <AppLayout>
      <template #main>
        <div class="step-bar">
          <div
            v-for="n in totalSteps"
            :key="n"
            class="step-dot"
            :class="{ on: n <= step }"
          />
        </div>

        <!-- Step 1 — Product -->
        <f7-block-title v-if="step === 1">{{
          $t("common.selectProduct")
        }}</f7-block-title>
        <f7-block v-if="step === 1">
          <AdProductGrid v-model="form.product" />
        </f7-block>

        <!-- WALLET flow: Step 2 = targeting, Step 3 = creative, Step 4 = budget+review -->
        <template v-if="billingKind === 'WALLET' && step === 2">
          <AdTargetingPicker v-model="targeting" />
        </template>

        <template v-if="billingKind === 'WALLET' && step === 3">
          <f7-block-title>{{ $t("common.creative") }}</f7-block-title>
          <f7-list inset strong>
            <f7-list-input
              :label="$t('common.title')"
              type="text"
              maxlength="60"
              :value="form.title"
              @input="form.title = $event.target.value"
            />
            <f7-list-input
              :label="$t('common.messageBody')"
              maxlength="150"
              :value="form.body"
             
          @texteditor:change="(v) => (form.body = v)"
          resizable
          required
          type="texteditor"
          :text-editor-params="{
          mode: 'keyboard-toolbar',
        }"
            />
            <f7-list-input
              :label="$t('common.ctaLabel')"
              type="text"
              :value="form.ctaLabel"
              @input="form.ctaLabel = $event.target.value"
            />
            <f7-list-input
              :label="$t('common.ctaUrl')"
              type="text"
              :value="form.ctaUrl"
              @input="form.ctaUrl = $event.target.value"
            />
          </f7-list>
          <f7-block-title
            >{{ $t("common.documents") }} ({{
              $t("common.optional")
            }})</f7-block-title
          >
          <f7-block>
            <AttachmentPicker />
          </f7-block>
          <f7-block-title>{{ $t("common.preview") }}</f7-block-title>
          <f7-card class="preview-card">
            <div class="preview-head">
              <div class="preview-label">{{ $t("common.sponsored") }}</div>
              <div class="preview-title">
                {{ form.title || $t("common.defaultAdTitle") }}
              </div>
              <div class="preview-body">
                {{ form.body || $t("common.messageBody") }}
              </div>
            </div>
            <div class="preview-cta">
              {{ form.ctaLabel || $t("common.knowMore") }}
            </div>
          </f7-card>
        </template>

        <template v-if="billingKind === 'WALLET' && step === 4">
          <f7-block-title>{{ $t("common.budget") }}</f7-block-title>
          <f7-list inset strong>
            <f7-list-item
              :title="$t('common.priceTier')"
              smart-select
              :smart-select-params="{ openIn: 'sheet' }"
            >
              <select v-model="form.priceTier">
                <option value="ENTRY">{{ $t("common.starterPrice") }}</option>
                <option value="STANDARD">
                  {{ $t("common.standardPrice") }}
                </option>
                <option value="PREMIUM">{{ $t("common.premiumPrice") }}</option>
              </select>
            </f7-list-item>
            <f7-list-input
              :label="$t('common.dailyBudget')"
              type="number"
              :value="form.dailyBudget"
              @input="form.dailyBudget = $event.target.value"
            />
            <f7-list-input
              :label="$t('common.totalBudget')"
              type="number"
              :value="form.totalBudget"
              @input="form.totalBudget = $event.target.value"
            />
          </f7-list>
          <f7-block class="review-block">
            <div class="review-row">
              <span>{{ $t("common.product") }}</span
              ><strong>{{ productMeta(form.product)?.label }}</strong>
            </div>
            <div class="review-row">
              <span>{{ $t("common.product") }}</span
              ><strong>{{ productMeta(form.product)?.label }}</strong>
            </div>
            <div class="review-row">
              <span>{{ $t("common.tarif") }} ({{ form.priceTier }})</span
              ><strong
                >{{
                  fmtCfa(
                    productMeta(form.product)?.rates?.[form.priceTier] ?? 0,
                  )
                }}
                {{ productMeta(form.product)?.unit }}</strong
              >
            </div>
            <div class="review-row">
              <span>{{ $t("common.segments") }}</span
              ><strong>{{ targeting.adTargetingCategories.length }}</strong>
            </div>
            <div class="review-row">
              <span>{{ $t("common.walletBalance") }}</span
              ><strong>{{ fmtCfa(walletBalance) }}</strong>
            </div>
          </f7-block>
        </template>

        <!-- INVOICE flow: Step 2 = contract terms + review -->
        <template v-if="billingKind === 'INVOICE' && step === 2">
          <f7-block-title>{{ $t("common.contractTerms") }}</f7-block-title>
          <f7-list inset strong>
            <f7-list-item
              v-if="form.product === 'CATEGORY_PARTNER'"
              :title="$t('ad.exclusiveCategory')"
              smart-select
              :smart-select-params="{ openIn: 'sheet' }"
            >
              <select v-model="form.exclusiveCategoryCode">
                <option
                  v-for="c in GROUP_CATEGORIES"
                  :key="c.code"
                  :value="c.code"
                >
                  {{ c.label }}
                </option>
              </select>
            </f7-list-item>
            <f7-list-item
              :title="$t('common.priceTier')"
              smart-select
              :smart-select-params="{ openIn: 'sheet' }"
            >
              <select v-model="form.priceTier">
                <option value="ENTRY">{{ $t("common.starterPrice") }}</option>
                <option value="STANDARD">
                  {{ $t("common.standardPrice") }}
                </option>
                <option value="PREMIUM">{{ $t("common.premiumPrice") }}</option>
              </select>
            </f7-list-item>
            <f7-list-item
              :title="$t('common.country')"
              :text="$t('adCreate.defaultCountry')"
            />
          </f7-list>
          <f7-block class="review-block">
            <div class="review-row">
              <span>{{ $t("common.product") }}</span
              ><strong>{{ productMeta(form.product)?.label }}</strong>
            </div>
            <div class="review-row">
              <span>{{ $t("common.product") }}</span
              ><strong>{{ productMeta(form.product)?.label }}</strong>
            </div>
            <div class="review-row">
              <span>{{ $t("common.amount") }}</span
              ><strong
                >{{
                  fmtCfa(
                    productMeta(form.product)?.rates?.[form.priceTier] ?? 0,
                  )
                }}
                /
                {{
                  form.product === "CATEGORY_PARTNER" ? $t("common.year") : $t("common.month")
                }}</strong
              >
            </div>
          </f7-block>
          <f7-block class="invoice-note">
            <f7-icon f7="doc_text_fill" size="14" />
            {{ $t("ad.invoiceNote") }}
          </f7-block>
        </template>

<f7-toolbar bottom class="no-hairlines">
          <div class="grid grid-cols-2 gap-2 width-100">
            <f7-button v-if="step > 1" large outline @click="step--" class="col">{{ $t("common.back") }}</f7-button>
            <f7-button
              large
              fill
              :disabled="!canContinue"
              @click="step < totalSteps ? step++ : submit()"
              class="col"
            >
              {{
                step < totalSteps
                  ? $t("group.creationNextStep")
                  : billingKind === "INVOICE"
                  ? $t("common.requestQuote")
                  : $t("campaign.launchCampaign")
              }}
            </f7-button>
          </div>
        </f7-toolbar>
      </template>
    </AppLayout>
  </f7-page>
</template>

<script setup>
import { reactive, ref, computed } from "vue";
import { f7 } from "framework7-vue";
import AppLayout from "../components/common/AppLayout.vue";
import AdProductGrid from "../components/ads/AdProductGrid.vue";
import AdTargetingPicker from "../components/ads/AdTargetingPicker.vue";
import AttachmentPicker from "../components/feed/AttachmentPicker.vue";
import {
  productMeta,
  billingKindFor,
  fmtCfa,
} from "../constants/ad-products.js";
import { GROUP_CATEGORIES } from "../constants/group-taxonomy.js";
import { useAdsStore } from "../js/pinia-stores/ads.js";
import { useKeycloakStore } from "../js/pinia-stores/keycloak.js";
import { useAuthStore } from "../js/pinia-stores/auth.js";
import i18n from "../js/i18n.js";
const { t } = i18n.global;
const adsStore = useAdsStore();
const keycloakStore = useKeycloakStore();
const authStore = useAuthStore();
const step = ref(1);
const walletBalance = ref(38000);

const form = reactive({
  product: null,
  priceTier: "STANDARD",
  title: "",
  body: "",
  ctaLabel: "",
  ctaUrl: "",
  dailyBudgetCfa: "",
  totalBudgetCfa: "",
  exclusiveCategoryCode: null,
});

let targeting = reactive({
  adTargetingCategories: [],
  groupCategories: [],
  groupTypes: [],
  requiredGroupTags: [],
  excludedGroupTags: [],
  groupMinMembers: "",
  groupMaxMembers: "",
  eventWithinDays: "",
  cities: [],
  countryCodes: ["CI"],
  languages: [],
  placements: [],
});

const billingKind = computed(() => billingKindFor(form.product));
const totalSteps = computed(() => (billingKind.value === "INVOICE" ? 2 : 4));

const canContinue = computed(() => {
  if (step.value === 1) return !!form.product;
  if (billingKind.value === "WALLET" && step.value === 2)
    return targeting.adTargetingCategories.length > 0;
  if (billingKind.value === "WALLET" && step.value === 3)
    return form.title.trim().length > 0;
  return true;
});

async function submit() {
  f7.preloader.show();
  try {
    if (billingKind.value === "INVOICE") {
      const endpoint =
        form.product === "CATEGORY_PARTNER"
          ? "category-partner"
          : "group-intelligence";
      await adsStore.createSubscription(endpoint, {
        advertiserId: keycloakStore.subject,
        tier: form.priceTier,
        groupCategoryCode: form.exclusiveCategoryCode,
        countryCode: "CI",
      });
      f7.preloader.hide();
      f7.dialog.alert(t("ads.invoicingRequestSent"));
      f7.router.navigate("/ads");
      return;
    }

    const shortId = f7.utils.id();
    if (authStore.documentsUrl.length > 0) {
      await authStore.postR2Document({
        shortId,
        bucketName: import.meta.env.VITE_R2_PUBLIC_BUCKET_NAME,
      });
    }
    const campaign = await adsStore.createCampaign({
      advertiserId: keycloakStore.subject,
      name: form.title || productMeta(form.product)?.label,
      bidModel: form.product.includes("CPC") ? "CPC" : "CPM",
      bidAmountCfa: productMeta(form.product)?.rates?.[form.priceTier] ?? 0,
      dailyBudgetCfa: Number(form.dailyBudgetCfa),
      totalBudgetCfa: Number(form.totalBudgetCfa),
      targeting,
      creative: {
        title: form.title,
        body: form.body,
        ctaLabel: form.ctaLabel,
        ctaUrl: form.ctaUrl,
        format: "NATIVE_FEED",
        media: authStore.documentsUrl,
      },
    });
    await adsStore.activate(campaign.id);
    f7.preloader.hide();
    authStore.clearDocumentsUrl();
    f7.router.navigate("/ads");
  } catch (e) {
    f7.preloader.hide();
    f7.dialog.alert(e?.response?.data?.message || t("common.creationFailed"));
  }
}
</script>

<style scoped>
.step-label {
  font-size: 12px;
  color: var(--f7-theme-color);
  font-weight: 500;
}
.step-bar {
  display: flex;
  gap: 4px;
  padding: 8px 16px 0;
}
.step-dot {
  flex: 1;
  height: 3px;
  border-radius: 2px;
  background: var(--f7-list-item-border-color, #e0e0e0);
}
.step-dot.on {
  background: var(--f7-theme-color);
}
.review-block,
.invoice-note {
  background: var(--f7-list-bg-color, #f5f5f5);
  border-radius: 10px;
}
.review-row {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  padding: 6px 0;
  border-bottom: 1px solid var(--f7-list-item-border-color, #e0e0e0);
}
.review-row:last-child {
  border-bottom: none;
}
.invoice-note {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.5;
}
.preview-card {
  margin: 0;
}
.preview-head {
  background: var(--f7-theme-color);
  color: #fff;
  padding: 10px 12px;
  border-radius: 8px 8px 0 0;
}
.preview-label {
  font-size: 9px;
  opacity: 0.6;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.preview-title {
  font-size: 13px;
  font-weight: 500;
  margin-top: 2px;
}
.preview-body {
  font-size: 11px;
  opacity: 0.85;
  margin-top: 2px;
}
.preview-cta {
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 500;
  color: var(--f7-theme-color);
}
</style>
